import { EntityType } from "../../api/grpc/subscription/subscription";
import {
  ProductStoreAddOnRule,
  ProductStoreFeaturedData,
  ProductStorePriceDescriptor,
  ProductStorePurchaseRule,
  ProductStoreSectionData,
} from "./ProductStore.types";

// Prices come from the customer's Chargebee prices (getCustomerProductStore).
// Cards without `pricing` show "Price on request".
const GOGET_ONE_PRICING: ProductStorePriceDescriptor = {
  priceId: "goget-one-v2-{currency}",
  text: "{price} + shipping",
};

// Order dialog rules, from the "Product store — purchase and add-on rules"
// spec. Shipping is one per order, whatever the quantity.
const INTERNATIONAL_SHIPPING = "Shipping-International-V3-{currency}";

const TOTAL_CARE_ADD_ON: ProductStoreAddOnRule = {
  productId: "total-care-support",
  quantity: "fixed",
  recommended: true,
};

// One warranty per panel or bundle. PREMIUM CARE needs CARE.
const WARRANTY_ADD_ONS: ProductStoreAddOnRule[] = [
  { productId: "extended-care", quantity: "matchMain", recommended: true },
  {
    productId: "premium-care",
    quantity: "matchMain",
    requires: "extended-care",
  },
];

const GOGET_ONE_PURCHASE: ProductStorePurchaseRule = {
  quantity: { mode: "selectable", label: "devices" },
  shipping: INTERNATIONAL_SHIPPING,
  addOns: [...WARRANTY_ADD_ONS, TOTAL_CARE_ADD_ON],
};

const SENSOR_PURCHASE: ProductStorePurchaseRule = {
  quantity: { mode: "selectable", label: "packs" },
  shipping: "Shipping-Sensor-{currency}",
  addOns: [TOTAL_CARE_ADD_ON],
};

const WARRANTY_ONLY_MESSAGE =
  "can only be purchased together with a new GOGET One room panel.";

export const PRODUCT_STORE_FILTERS = [
  { id: "all" as const, label: "All products" },
  { id: "room-panels" as const, label: "Room panels" },
  { id: "sensors" as const, label: "Sensors" },
  { id: "media-player" as const, label: "Media player" },
  { id: "services" as const, label: "Services" },
];

export const PRODUCT_STORE_FEATURED: ProductStoreFeaturedData = {
  id: "featured-goget-one",
  eyebrow: "Featured hardware",
  title: "GOGET One (2nd Gen) Room Panel",
  description:
    "Soft Touch White 10” room panel with LED status lights, PoE, Wi-Fi, industrial Android 14 and IEEE 802.1X LAN support.",
  features: ["Deployment-ready", "Global delivery", "One-off purchase"],
  pricing: GOGET_ONE_PRICING,
  purchase: GOGET_ONE_PURCHASE,
  primaryAction: { type: "checkout" },
  secondaryAction: { type: "details", targetId: "goget-one-room-panel" },
};

export const PRODUCT_STORE_SECTIONS: ProductStoreSectionData[] = [
  {
    id: "room-panels",
    label: "Room Panel",
    description: "One-off purchases and warranty extensions",
    products: [
      {
        id: "goget-one-room-panel",
        artwork: "room-panel",
        artworkAlt: "GOGET One second generation room panel",
        eyebrow: "One-off purchase",
        title: "GOGET One (2nd Gen) Room Panel",
        description: "Soft Touch White 10” · LED · PoE · WiFi",
        detail: "Android 14 industrial · IEEE 802.1X LAN",
        pricing: GOGET_ONE_PRICING,
        ctaLabel: "Order now",
        action: { type: "checkout" },
        layout: "horizontal",
        unitLabel: "per device",
        purchase: GOGET_ONE_PURCHASE,
      },
      {
        id: "goget-one-sensor-bundle",
        artwork: "room-panel-sensor",
        artworkAlt: "GOGET One room panel with occupancy sensor",
        eyebrow: "Automatic edition",
        eyebrowTone: "blue",
        secondaryEyebrow: "One-off purchase",
        title: "GOGET One (2nd Gen) + Sensor",
        description: "Room Panel Automatic Edition with",
        detail: "1× occupancy sensor included.",
        pricing: {
          priceId: "GOGET-One-2nd-Gen-Sensor-{currency}",
          text: "{price} + shipping",
        },
        ctaLabel: "Order now",
        action: { type: "checkout" },
        layout: "horizontal",
        unitLabel: "per bundle",
        purchase: {
          quantity: { mode: "selectable", label: "bundles" },
          shipping: "Shipping-GOGET-One-2nd-Gen-Sensor-{currency}",
          addOns: [...WARRANTY_ADD_ONS, TOTAL_CARE_ADD_ON],
        },
      },
      {
        id: "extended-care",
        artwork: "extended-care",
        artworkAlt: "Extended CARE warranty shield",
        eyebrow: "Extended warranty",
        eyebrowTone: "gold",
        title: "Extended CARE Warranty",
        description: "GOGET One 2nd Gen warranty extension",
        detail: "to a total of 3 years.",
        pricing: {
          priceId: "Extended-CARE-Warranty-GOGET-One-2nd-Gen-{currency}",
          text: "{price}",
        },
        ctaLabel: "Order now",
        action: { type: "contact" },
        layout: "horizontal",
        unitLabel: "per device",
        highlights: ["3 years of warranty in total", "For GOGET One (2nd Gen)"],
        addOnOnlyMessage: `Extended CARE Warranty ${WARRANTY_ONLY_MESSAGE}`,
      },
      {
        id: "premium-care",
        artwork: "premium-care",
        artworkAlt: "Extended PREMIUM CARE warranty shield",
        eyebrow: "Premium extended warranty",
        eyebrowTone: "purple",
        title: "Extended PREMIUM CARE",
        description: "4 years total + advance replacement.",
        note: { text: "Requires", emphasis: "Extended CARE Warranty." },
        pricing: {
          priceId:
            "Extended-PREMIUM-CARE-Warranty-GOGET-One-2nd-Gen-{currency}",
          text: "{price}",
        },
        priceNote: "Terms apply. Available in selected markets.",
        ctaLabel: "Contact sales",
        action: { type: "contact" },
        layout: "horizontal",
        unitLabel: "per device",
        highlights: ["4 years of warranty in total", "Advance replacement"],
        addOnOnlyMessage: `Extended PREMIUM CARE Warranty ${WARRANTY_ONLY_MESSAGE}`,
      },
    ],
  },
  {
    id: "sensors",
    label: "Sensors",
    description: "Workspace occupancy sensing with service activation",
    products: [
      {
        id: "occupancy-sensor-one",
        artwork: "sensor-one",
        artworkAlt: "Single GOGET workspace occupancy sensor",
        eyebrow: "1-pack",
        eyebrowTone: "blue",
        title: "Workspace Occupancy Sensor (PIR)",
        description: "Gateway-free PIR sensor for GOGET One, including",
        detail: "service activation · single unit.",
        pricing: {
          priceId: "Workspace-Occupancy-Sensor-PIR---1-pack-{currency}",
          text: "{price} + shipping",
        },
        ctaLabel: "Order now",
        action: { type: "checkout" },
        layout: "horizontal",
        unitLabel: "per pack",
        purchase: SENSOR_PURCHASE,
      },
      {
        id: "occupancy-sensor-four",
        artwork: "sensor-four",
        artworkAlt: "Four GOGET workspace occupancy sensors",
        eyebrow: "4-pack",
        eyebrowTone: "teal",
        title: "Workspace Occupancy Sensors (PIR)",
        description: "PIR sensor for GOGET, including",
        detail: "service activation · four units.",
        pricing: {
          priceId: "Workspace-Occupancy-Sensors-PIR---4-pack-{currency}",
          text: "{price} + shipping",
        },
        ctaLabel: "Order now",
        action: { type: "checkout" },
        layout: "horizontal",
        unitLabel: "per 4-pack",
        purchase: SENSOR_PURCHASE,
      },
    ],
  },
  {
    id: "media-player",
    label: "Media Player – Deployment-Ready",
    description: "One-off purchase, configured for production use",
    products: [
      {
        id: "iadea-media-player",
        artwork: "media-player",
        artworkAlt: "IAdea media player with Wayfinder X configuration",
        eyebrow: "Just add monitor & deploy",
        secondaryEyebrow: "Special order — not stocked",
        title:
          "IAdea XMP-8552 Media Player – Wayfinder X production-ready incl device management configuration",
        description:
          "Wayfinder X production-ready media player, including device management",
        detail: "configuration for a faster, predictable deployment.",
        pricing: {
          priceId: "IAdea-XMP-8552-Media-Player-{currency}",
          text: "{price} + shipping",
        },
        ctaLabel: "Order now",
        action: { type: "checkout" },
        layout: "wide",
        unitLabel: "per player",
        purchase: {
          quantity: { mode: "selectable", label: "players" },
          shipping: INTERNATIONAL_SHIPPING,
          addOns: [
            {
              productId: "floor-map-artwork",
              quantity: "independent",
              quantityLabel: "floors",
            },
            TOTAL_CARE_ADD_ON,
          ],
        },
      },
    ],
  },
  {
    id: "services",
    label: "Services",
    description: "Specialist services and subscription add-ons",
    products: [
      {
        id: "floor-map-artwork",
        artwork: "floor-map",
        artworkAlt: "Detailed GOGET 2D floor map artwork",
        eyebrow: "Professional service",
        eyebrowTone: "blue",
        title: "2D/3D floor map artwork",
        description: "Per floor, including up to 2 revisions.",
        pricing: { priceId: "floor-plan-v1-{currency}", text: "{price}" },
        ctaLabel: "Order now",
        action: { type: "checkout" },
        layout: "service",
        unitLabel: "per floor",
        highlights: [
          "2D/3D artwork of one floor",
          "Up to 2 revisions included",
        ],
        purchase: {
          quantity: { mode: "selectable", label: "floors" },
          addOns: [TOTAL_CARE_ADD_ON],
        },
      },
      {
        id: "cross-org-calendars",
        artwork: "cross-org",
        artworkAlt: "Cross-organization shared calendar service",
        eyebrow: "Subscription add-on",
        eyebrowTone: "purple",
        title: "Cross-Org Shared Calendars",
        description: "For co-working and shared room panels.",
        note: { text: "Requires", emphasis: "BusinessPLUS plan." },
        pricing: {
          priceId: "Cross-Org-Shared-Room-Calendars-1-Year-{currency}-Yearly",
          text: "{price}/full year · (pay for days left)",
          threeYear: {
            priceId:
              "Cross-Org-Shared-Room-Calendars-3-Year-{currency}-Every-3-years",
            text: "{price}/3 years · (pay for days left)",
          },
        },
        ctaLabel: "Order now",
        action: { type: "subscription" },
        entityType: EntityType.CROSS_ORG_ROOM_CALENDARS,
        layout: "service",
        purchase: {
          quantity: { mode: "fixed" },
          addOns: [TOTAL_CARE_ADD_ON],
          requiresPlan: "BusinessPLUS",
        },
      },
      {
        id: "total-care-support",
        artwork: "total-care",
        artworkAlt: "Total Care support and managed services",
        eyebrow: "Subscription add-on",
        eyebrowTone: "teal",
        title: "Total Care Support",
        description:
          "Hands-on technical assistance for Microsoft 365 and Google Workspace, with managed services for configuration, setup, and monitoring.",
        pricing: {
          priceId: "Total-Care-Support-1-Year-{currency}-Yearly",
          text: "{price}/full year · (pay for days left)",
          threeYear: {
            priceId: "Total-Care-Support-3-Year-{currency}-Every-3-years",
            text: "{price}/3 years · (pay for days left)",
          },
        },
        ctaLabel: "Order now",
        action: { type: "subscription" },
        entityType: EntityType.TOTAL_CARE_SUPPORT,
        layout: "service",
        highlights: [
          "Hands-on help with Microsoft 365 and Google Workspace",
          "Managed configuration, setup and monitoring",
        ],
      },
    ],
  },
];
